import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { userService, technicianService, teamsService } from '../../services/api';

const UserForm = ({ open, onClose, onSubmit, user, technicians, users, teams }) => {
  const formik = useFormik({
    initialValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      password: '',
      role: user?.role || 'VIEWER',
      technicianId: user?.technicianId || '',
      teamId: user?.teamId || ''
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Prénom requis'),
      lastName: Yup.string().required('Nom requis'),
      email: Yup.string().email('Email invalide').required('Email requis'),
      password: user ? Yup.string() : Yup.string().required('Mot de passe requis'),
      role: Yup.string().required('Rôle requis')
    }),
    onSubmit: async (values) => {
      onSubmit(values);
      formik.resetForm();
    },
    enableReinitialize: true
  });

  const handleTechnicianChange = (event) => {
    const technicianId = event.target.value;
    formik.setFieldValue('technicianId', technicianId);
  };

  const isTechnicianAssigned = (technicianId) => {
    return users.some(u => u.id !== user?.id && u.technicianId === technicianId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="firstName"
                label="Prénom"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
              />
              <TextField
                name="lastName"
                label="Nom"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                fullWidth
              />
            </Box>
            
            <TextField
              name="email"
              label="Email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
            />
            
            <TextField
              name="password"
              label={user ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Rôle</InputLabel>
              <Select
                name="role"
                value={formik.values.role}
                onChange={formik.handleChange}
                label="Rôle"
              >
                <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="VIEWER">Utilisateur</MenuItem>
              </Select>
            </FormControl>

                        <FormControl fullWidth>
              <InputLabel>Technicien associé (optionnel)</InputLabel>
              <Select
                name="technicianId"
                value={formik.values.technicianId}
                onChange={handleTechnicianChange}
                label="Technicien associé (optionnel)"
              >
                <MenuItem value="">Aucun technicien</MenuItem>
                {technicians
                  .filter(tech => tech.isActive && !isTechnicianAssigned(tech.id))
                  .map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.firstName} {tech.lastName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Équipe (optionnel)</InputLabel>
              <Select
                name="teamId"
                value={formik.values.teamId}
                onChange={formik.handleChange}
                label="Équipe (optionnel)"
              >
                <MenuItem value="">Aucune équipe</MenuItem>
                {teams?.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name} - {team.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button type="submit" variant="contained">
            {user ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [teams, setTeams] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const loadData = useCallback(async () => {
    try {
      const [usersData, techniciansData, teamsData] = await Promise.all([
        userService.getAll(),
        technicianService.getAll(),
        teamsService.getAll()
      ]);
      
      setUsers(usersData);
      setTechnicians(techniciansData);
      setTeams(teamsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      showNotification('Erreur lors du chargement des données', 'error');
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateUser = async (userData) => {
    try {
      await userService.create(userData);
      setFormOpen(false);
      loadData();
      showNotification('Utilisateur créé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      showNotification('Erreur lors de la création de l\'utilisateur', 'error');
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      await userService.update(selectedUser.id, userData);
      setFormOpen(false);
      setSelectedUser(null);
      loadData();
      showNotification('Utilisateur modifié avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      showNotification('Erreur lors de la modification de l\'utilisateur', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await userService.delete(userId);
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      loadData();
      showNotification('Utilisateur supprimé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showNotification('Erreur lors de la suppression de l\'utilisateur', 'error');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'error';
      case 'ADMIN': return 'warning';
      case 'VIEWER': return 'info';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'Super Admin';
      case 'ADMIN': return 'Admin';
      case 'VIEWER': return 'Utilisateur';
      default: return role;
    }
  };

  const getTechnicianName = (technicianId) => {
    if (!technicianId) return null;
    const technician = technicians.find(t => t.id === technicianId);
    return technician ? `${technician.firstName} ${technician.lastName}` : null;
  };

  // Fonction de filtrage des utilisateurs
  const getFilteredUsers = () => {
    return users.filter(user => {
      const technicianName = user.technicianId ? getTechnicianName(user.technicianId) : '';
      
      // Filtrage par recherche textuelle
      const searchTerms = searchQuery.toLowerCase().split(' ');
      const searchMatch = searchTerms.every(term => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const role = getRoleLabel(user.role).toLowerCase();
        
        return fullName.includes(term) || 
               email.includes(term) || 
               role.includes(term) ||
               technicianName.toLowerCase().includes(term);
      });
      
      return searchMatch;
    });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Gestion des utilisateurs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
        >
          Nouvel utilisateur
        </Button>
      </Box>

      {/* Barre de recherche */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom, email, rôle ou technicien..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              {getFilteredUsers().length} / {users.length} utilisateurs
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'background.paper' }}>Utilisateur</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper' }}>Email</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper' }}>Rôle</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper' }}>Technicien associé</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const filteredUsers = getFilteredUsers();
              
              if (filteredUsers.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchQuery ? 
                          'Aucun utilisateur ne correspond aux critères de recherche.' :
                          'Aucun utilisateur trouvé.'
                        }
                      </Typography>
                      {searchQuery && (
                        <Button 
                          variant="text" 
                          onClick={() => setSearchQuery('')}
                          sx={{ mt: 1 }}
                        >
                          Effacer les filtres
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }
              
              return filteredUsers.map((user) => {
                const technicianName = getTechnicianName(user.technicianId);
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="body1">
                          {`${user.firstName} ${user.lastName}`}
                          </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {technicianName ? (
                            <Chip
                          label={technicianName}
                              variant="outlined"
                                  size="small"
                        />
                        ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucun
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifier">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setFormOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          onClick={() => {
                            setUserToDelete(user);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              });
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      <UserForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}
        user={selectedUser}
        technicians={technicians}
        users={users}
        teams={teams}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'utilisateur "{userToDelete?.firstName} {userToDelete?.lastName}" ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button 
            onClick={() => handleDeleteUser(userToDelete.id)}
            color="error"
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;